import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Layout, Select, message } from 'antd';
import ResultTable from './ResultTable';
import { PageContent, PageSpace } from '../styled';
import style from '../SummaryTab/index.scss';
import { returnFraudUserAccessByResource } from '../../../../util/TmFraudModuleFilter';
import {
  SCENARIO_RESULT_FAILED,
  DEFAULT_PAGINATION_PROPS,
  UNIFIED_ALERTS_SOURCE_IDS,
  ALERT__REQUEST_SOURCE_ID,
  REVIEW_REQUEST_STATUS,
  UNIFIED_ALERTS_SOURCE_IDS_FRAUD,
  UNIFIED_ALERTS_SOURCE_IDS_TM,
  UNIFIED_ALERTS_SOURCE_IDS_PREVENTION,
  UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION
} from './constants';
import FilterComponent, {
  handleFilterPopup,
  handleSortPopup
} from '../../../common/FilterComponent';
import { sortColumnProps, sortDataProps } from './sortProps';
import { hasAttributeValue } from '../../../../util/index';
import PreventionResultTable from './PreventionResultTable';
import { isResourceAvailable } from '../../../../util/index';
import { getSelectedResources } from '../../../../util/resourceManager';
import { btoaUTF8 } from '../../../common/utils';
import { isEmpty } from '../../../../util/lodashlike';

const { Content } = Layout;
const { trmResources } = getSelectedResources();

const Result = ({
  scenarioResults,
  isLoading,
  error,
  resultAPIActions,
  authentication,
  trmActions,
  alertLevels
}) => {
  const {
    getAMLSCommonUnifiedAlerts,
    putAMLSCommonAlertPartyUpdate,
    fetchTypologiesActions,
    fetchAMLSAlertLevel
  } = resultAPIActions;
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_PROPS);
  const { current, pageSize } = pagination;
  const [sortFilterObj, setSortFilterObj] = useState({
    filter: [],
    sort: { columnName: 'alertDate', operator: 'DESC' }
  });
  const [isFraud, setIsFraud] = useState(false);
  const [isTMuser, setIsTMuser] = useState(false);
  const [isRTuser, setIsRTuser] = useState(false);
  const [isPrevention, setIsPrevention] = useState(false);
  const [currentSelectedFilter, setCurrentSelectedFilter] = useState();

  const fetchUnifiedAlerts = ({ page, pageSize, sourceId }) => {
    getAMLSCommonUnifiedAlerts({
      sourceIds: sourceId,
      offset: (page - 1) * pageSize,
      limit: pageSize,
      q: btoaUTF8(JSON.stringify(sortFilterObj))
    });
  };

  useEffect(() => {
    const userAccess = returnFraudUserAccessByResource(
      authentication.data,
      trmResources.APP_3_RESOURCE_UI_AMLS_COMMON_ALERTS
    );
    setIsFraud(userAccess.isFraud);
    setIsTMuser(userAccess.isTMuser);
    setIsRTuser(userAccess.isRTUser);
    const preventionResourceAccess = hasAttributeValue(
      authentication,
      trmResources.APP_3_RESOURCE_UI_RUN_INSTANCE,
      '24'
    );

    const detectionResourceAccess = hasAttributeValue(
      authentication,
      trmResources.APP_3_RESOURCE_UI_RUN_INSTANCE,
      '8'
    );

    const fraudResourceAccess = hasAttributeValue(
      authentication,
      trmResources.APP_3_RESOURCE_UI_RUN_INSTANCE,
      '20'
    );

    setIsFraud(fraudResourceAccess);
    setIsTMuser(detectionResourceAccess);
    setIsPrevention(preventionResourceAccess);
  }, [authentication]);

  useEffect(() => {
    if (isPrevention) {
      setCurrentSelectedFilter('PR');
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS_PREVENTION;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
    } else if (!isFraud && isTMuser) {
      setCurrentSelectedFilter('TM');
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS_TM;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
    } else if (isFraud && !isTMuser) {
      setCurrentSelectedFilter('Fraud');
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS_FRAUD;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
    } else if (isFraud && isTMuser) {
      setCurrentSelectedFilter('All');
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
    } else if (!isFraud && !isTMuser && isRTuser) {
      setCurrentSelectedFilter('Fraud');
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
    }
  }, [isPrevention, isFraud, isTMuser]);

  useEffect(() => {
    if (isPrevention) {
      setCurrentSelectedFilter('PR');
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS_PREVENTION;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
    } else if (currentSelectedFilter === 'TM') {
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS_TM;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
    } else if (currentSelectedFilter === 'Fraud') {
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS_FRAUD;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
    } else if (currentSelectedFilter === 'All') {
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
    }
  }, [sortFilterObj, current, pageSize]);

  useEffect(() => {
    if (!isEmpty(error) && !isLoading) {
      message.error(SCENARIO_RESULT_FAILED, 1.5);
    }
  }, [error]);

  const onPaginationChange = useCallback((page, pageSize) => {
    setPagination({ current: page, pageSize });
  });

  // FIN-13287 — the Level column filter must go through this same server-side
  // path (sortFilterObj.filter -> q, refetched below), not antd's client-side
  // onFilter: the grid only ever holds one page, so a client filter empties the
  // body the moment the matching rows are not on the loaded page.
  //
  // [adversarial S3(b)] MERGE rather than replace: `sortFilterObj.filter` can
  // already carry entries FilterComponent's own popup wrote via `listenFilters`
  // below. Stomping the whole array here would silently drop the user's other
  // filters the moment they touch the Level column.
  const onLevelFilterChange = (values = []) => {
    setPagination(DEFAULT_PAGINATION_PROPS);
    setSortFilterObj((prev) => {
      const others = (prev.filter || []).filter(
        (f) => f.columnName !== 'alertLevel'
      );
      // [adversarial S3(a)] filterMultiple is set to false on the column
      // (PreventionResultTable.js) specifically so `values` never carries more
      // than one code here — a comma-joined multi-value would silently match
      // zero rows against the BE's single-value `alertLevel` branch.
      const next = values.length
        ? [
            ...others,
            { columnName: 'alertLevel', logic: '=', value: values[0] }
          ]
        : others;
      return { ...prev, filter: next };
    });
  };

  const listenFilters = (data = []) => {
    setPagination(DEFAULT_PAGINATION_PROPS);
    const filter = data.map(({ filter, logic, item }) => {
      const strValues = [item?.val].flat().join();
      return { columnName: filter?.val, logic: logic?.val, value: strValues };
    });
    setSortFilterObj((pre) => ({ ...pre, filter }));
    handleFilterPopup(); // close Filter popup
  };

  const listenSort = (data = []) => {
    setPagination(DEFAULT_PAGINATION_PROPS);
    const sort = data.length
      ? { columnName: data[0].id, operator: data[0].defaultValue }
      : {};
    setSortFilterObj((pre) => ({ ...pre, sort }));
    handleSortPopup && handleSortPopup(); // close Sort popup
  };

  const triggerBulkStatusUpdate = (alertId, alertSourceId) => {
    if (isEmpty(alertId)) {
      return;
    }
    putAMLSCommonAlertPartyUpdate([
      {
        alertId,
        sourceId: alertSourceId
          ? Number(alertSourceId)
          : ALERT__REQUEST_SOURCE_ID,
        status: REVIEW_REQUEST_STATUS
      }
    ]);
  };

  const onDropDownFilterChange = (selectedValue) => {
    setPagination(DEFAULT_PAGINATION_PROPS);

    if (selectedValue === 'TM') {
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS_TM;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
      setCurrentSelectedFilter('TM');
    } else if (selectedValue === 'Fraud') {
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS_FRAUD;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
      setCurrentSelectedFilter('Fraud');
    } else if (selectedValue === 'All') {
      let sourceId = UNIFIED_ALERTS_SOURCE_IDS;
      fetchUnifiedAlerts({ page: current, pageSize, sourceId });
      setCurrentSelectedFilter('All');
    }
  };

  return (
    <Content>
      <PageSpace size={5} direction="vertical">
        <PageContent>
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '20px'
              }}>
              {isFraud && isTMuser && (
                <Select
                  options={[
                    { value: 'All', label: 'All' },
                    { value: 'TM', label: 'TM' },
                    { value: 'Fraud', label: 'Fraud' }
                  ]}
                  style={{ width: '150px' }}
                  defaultValue="All"
                  onChange={onDropDownFilterChange}
                  className={style.selectFilter}
                />
              )}
              <FilterComponent
                {...{ listenFilters, listenSort }}
                sortColumns={sortColumnProps}
                sortRows={sortDataProps}
                isLoading={isLoading}
                isResult={true}
              />
            </div>
          </div>
          {isPrevention ? (
            <PreventionResultTable
              isLoading={isLoading}
              results={scenarioResults?.payload}
              paginationProps={pagination}
              onPaginationChange={onPaginationChange}
              totalResults={scenarioResults?.total}
              onAlertIdClick={triggerBulkStatusUpdate}
              hasAccessToAlertDetails={isResourceAvailable(
                { data: authentication.data },
                trmResources.APP_3_RESOURCE_UI_AMLS_TM_ALERT_DETAILS
              )}
              typlogyActions={trmActions?.data}
              fetchTypologiesActions={fetchTypologiesActions}
              fetchAMLSAlertLevel={fetchAMLSAlertLevel}
              alertLevels={alertLevels}
              onLevelFilterChange={onLevelFilterChange}
              // [adversarial S3(c)] Controlled `filteredValue`, derived from the same
              // sortFilterObj the fetch itself reads — the single source of truth, so
              // the funnel icon can never desync from what is actually applied (the
              // FIN-13509 class of confusion this same file already names elsewhere).
              alertLevelFilterValue={sortFilterObj.filter
                .filter((f) => f.columnName === 'alertLevel')
                .map((f) => f.value)}
            />
          ) : (
            <ResultTable
              isLoading={isLoading}
              results={scenarioResults?.payload}
              paginationProps={pagination}
              onPaginationChange={onPaginationChange}
              totalResults={scenarioResults?.total}
              onAlertIdClick={triggerBulkStatusUpdate}
              hasAccessToAlertDetails={isResourceAvailable(
                { data: authentication.data },
                trmResources.APP_3_RESOURCE_UI_AMLS_TM_ALERT_DETAILS
              )}
              alertLevels={alertLevels}
            />
          )}
        </PageContent>
      </PageSpace>
    </Content>
  );
};

Result.propTypes = {
  scenarioResults: PropTypes.object,
  resultAPIActions: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  alertLevels: PropTypes.array,
  authentication: PropTypes.object,
  trmActions: PropTypes.object
};

export default Result;
